import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import { fetchGiftAmounts, deleteGiftAmount } from '../../Redux/Reducers/ConsultationReducer';
import toast from 'react-hot-toast';
import { usePermissions } from '../../context/AuthContext';

function ConsultationListTable() {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const { giftAmounts = [], paginationDetail = {} } = useSelector(
    (state) => state?.ConsultationReducer || {}
  );

  const [open, setOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState(null);
  const [filter, setFilter] = useState('All');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [''],
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const { page, pageSize } = paginationModel;

    let query = {
      page: page + 1,
      limit: pageSize,
    };

    if (filter !== 'All') {
      query = {
        page: 1,
        limit: 1000,
        category: filter,
      };
    }

    dispatch(fetchGiftAmounts(query));
  }, [dispatch, paginationModel, filter]);

  useEffect(() => {
    if (filter !== 'All') {
      const size = Math.max(giftAmounts.length, 10);
      setPaginationModel((p) => ({ ...p, page: 0, pageSize: size }));
    }
  }, [filter, giftAmounts.length]);

  const handleDelete = async () => {
    if (!selectedCouponId) return;
    try {
      await dispatch(deleteGiftAmount(selectedCouponId)).unwrap();
      toast.success('Gift Amount deleted successfully!');

      let query = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      };
      if (filter !== 'All') {
        query = { page: 1, limit: 1000, category: filter };
      }
      dispatch(fetchGiftAmounts(query));
    } catch (error) {
      toast.error(error.message || 'Failed to delete!');
    }
    setOpen(false);
    setSelectedCouponId(null);
  };

  const rows = (Array.isArray(giftAmounts) ? giftAmounts : []).map((item, index) => ({
    id: item._id,
    sNo: index + 1 + (filter === 'All' ? paginationModel.page * paginationModel.pageSize : 0),
    title: item?.title || 'N/A',
    actualAmount: item?.actualAmount || 0,
    giftAmount: item?.giftAmount || 0,
    category: item?.category || 'N/A',
    createdAt: item?.createdAt ? new Date(item?.createdAt).toLocaleString() : '',
    updatedAt: item?.updatedAt ? new Date(item?.updatedAt).toLocaleString() : '',
  }));

  const columns = [
    { field: 'sNo', headerName: 'S.No.', width: 100 },
    { field: 'title', headerName: 'Title', width: 250 },
    { field: 'actualAmount', headerName: 'Amount', width: 150 },
    { field: 'giftAmount', headerName: 'Gift Amount', width: 150 },
    { field: 'category', headerName: 'Category', width: 200 },
    { field: 'createdAt', headerName: 'Created At', width: 200 },
    { field: 'updatedAt', headerName: 'Updated At', width: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      renderCell: ({ row }) => (
        <div>
          {can('consultation_vouchers:edit') && (
            <Tooltip title="Edit Voucher">
              <IconButton component={Link} to={`/Concoupons-edit/${row.id}`}>
                <i className="icofont-edit text-info"></i>
              </IconButton>
            </Tooltip>
          )}
          {can('consultation_vouchers:delete') && (
            <Tooltip title="Delete Voucher">
              <IconButton
                onClick={() => {
                  setSelectedCouponId(row.id);
                  setOpen(true);
                }}
              >
                <i className="icofont-ui-delete text-danger"></i>
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1
          pagetitle="Consultation Vouchers List"
          righttitle="Create New"
          link="/consultationvoucher-add"
          routebutton={can('consultation_vouchers:add')}
        />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <FormControl sx={{ mb: 2, width: 300 }}>
                  <h5>Filter by Category</h5>
                  <Select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="First Recharge">First Recharge</MenuItem>
                    <MenuItem value="Second Recharge">Second Recharge</MenuItem>
                    <MenuItem value="Third Recharge">Third Recharge</MenuItem>
                    <MenuItem value="Fourth Recharge">Fourth Recharge</MenuItem>
                    <MenuItem value="Fifth Recharge">Fifth Recharge & onwards</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ width: 1 }}>
                  <Box sx={{ height: 500 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      getRowId={(row) => row.id}
                      disableColumnFilter
                      disableDensitySelector
                      paginationMode={filter === 'All' ? 'server' : 'client'}
                      rowCount={filter === 'All' ? paginationDetail?.totalDocs || 0 : rows.length}
                      pageSizeOptions={[10, 20, 50, 100, 1000]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={(model) => setPaginationModel(model)}
                      slots={{ toolbar: GridToolbar }}
                      filterModel={filterModel}
                      onFilterModelChange={(newModel) => setFilterModel(newModel)}
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibilityModel(newModel)
                      }
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this voucher?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpen(false)}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleDelete} color="error" autoFocus>
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ConsultationListTable;